import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Target, TrendingUp, Search, Phone, Mail, MapPin, Send, ArrowRight, Loader2, Clock, Tag, CheckCircle2 } from "lucide-react";

const euro = (n) => (n || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const daysSince = (iso) => { if (!iso) return null; const d = Math.floor((Date.now() - new Date(iso)) / 86400000); return d; };

export default function Prospects() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/prospects").then((r) => setRows(r.data || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => rows.filter((r) => !q || `${r.name} ${r.city || ""} ${(r.tags || []).join(" ")}`.toLowerCase().includes(q.toLowerCase())), [rows, q]);
  const totalPotential = useMemo(() => filtered.reduce((s, r) => s + (r.potential || 0), 0), [filtered]);

  const logCall = async (r) => {
    try { await api.post(`/clients/${r.id}/interactions`, { type: "call", direction: "out", content: "Relance prospect" }); toast.success(`Relance enregistrée pour ${r.name}`); load(); }
    catch { toast.error("Erreur"); }
  };
  const convert = async (r) => {
    try { await api.put(`/clients/${r.id}`, { name: r.name, email: r.email || "", phone: r.phone || "", city: r.city || "", tags: r.tags || [], status: "actif", client_type: r.client_type || "pro", potential_budget: r.potential_budget || 0 }); toast.success(`${r.name} converti en client actif`); load(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div><h1 className="text-3xl lg:text-4xl font-display font-bold text-navy tracking-tight">Prospects</h1>
          <p className="text-slate-500 mt-1">Vos prospects à relancer, triés par potentiel de chiffre d'affaires.</p></div>
        <div className="bg-navy text-white rounded-2xl px-5 py-3" data-testid="prospects-total">
          <div className="text-[11px] text-cognac-300 uppercase tracking-wide">Potentiel total</div>
          <div className="text-2xl font-display font-bold tabular">{euro(totalPotential)}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-slate-200 max-w-md">
        <Search className="w-4 h-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un prospect…" data-testid="prospects-search" className="flex-1 bg-transparent text-sm focus:outline-none" />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-12 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-400" data-testid="prospects-empty">
          <Target className="w-10 h-10 mx-auto mb-3 opacity-40" />Aucun prospect. Passez un client au statut « Prospect » depuis sa fiche pour le suivre ici.
        </div>
      ) : (
        <div className="space-y-3" data-testid="prospects-list">
          {filtered.map((r, idx) => {
            const d = daysSince(r.last_interaction);
            const cold = d === null || d > 14;
            return (
              <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-4" data-testid={`prospect-row-${r.id}`}>
                <div className="w-9 h-9 rounded-full bg-navy text-cognac-300 flex items-center justify-center font-display font-bold text-sm shrink-0">{idx + 1}</div>
                <button onClick={() => navigate("/clients")} className="min-w-0 flex-1 text-left">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-navy truncate">{r.name}</span>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{r.client_type === "particulier" ? "Particulier" : "Pro"}</span>
                    {(r.tags || []).slice(0, 2).map((t) => <span key={t} className="inline-flex items-center gap-0.5 text-[10px] text-cognac-700 bg-cognac-50 border border-cognac-200 rounded-full px-1.5 py-0.5"><Tag className="w-2.5 h-2.5" />{t}</span>)}
                  </div>
                  <div className="text-[11px] text-slate-400 flex flex-wrap items-center gap-2 mt-0.5">
                    {r.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{r.city}</span>}
                    {r.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{r.phone}</span>}
                    {r.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{r.email}</span>}
                    <span className={`inline-flex items-center gap-1 ${cold ? "text-rose-500" : "text-emerald-600"}`}><Clock className="w-3 h-3" />{d === null ? "Jamais contacté" : `Relancé il y a ${d} j`}</span>
                  </div>
                </button>
                <div className="text-right shrink-0">
                  <div className="text-[11px] text-slate-400 inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Potentiel</div>
                  <div className="text-lg font-display font-bold text-cognac-600 tabular">{euro(r.potential)}</div>
                  <div className="text-[10px] text-slate-400">{r.pending_devis > 0 ? `dont ${euro(r.pending_devis)} devis` : "estimé"}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => logCall(r)} data-testid={`prospect-relance-${r.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:border-cognac-400"><Send className="w-4 h-4" /> Relancer</button>
                  <button onClick={() => convert(r)} data-testid={`prospect-convert-${r.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600"><CheckCircle2 className="w-4 h-4" /> Convertir</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
